import {
    computed,
    Directive,
    effect,
    EmbeddedViewRef,
    inject,
    input,
    ɵstringify as stringify,
    TemplateRef,
    ViewContainerRef
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FeatureFlagService, featuresEnabled } from "@dcc/shared/feature-flags/data-access";

@Directive({
    standalone: true,
    selector: "[featureFlag]"
})
export class FeatureFlagDirective {
    private featureFlagService = inject(FeatureFlagService);
    private vcr = inject(ViewContainerRef);
    private tpl = inject(TemplateRef);

    private readonly _thenTemplateRef: TemplateRef<unknown> = this.tpl;

    private _elseTemplateRef: TemplateRef<unknown> | null | undefined;
    private _elseViewRef: EmbeddedViewRef<unknown> | null | undefined;
    private _thenViewRef: EmbeddedViewRef<unknown> | null | undefined;

    public featureFlag = input<string | string[]>();
    public featureFlagOr = input<string | string[] | undefined>();
    public featureFlagElse = input<TemplateRef<unknown> | null>(null);

    private flags = toSignal(this.featureFlagService.load(), { initialValue: {} });

    private featureEnabled = computed(() => {
        const flags = this.flags();
        const flagsFilter = this.featureFlag() ?? [];
        const orFlagsFilter = this.featureFlagOr() ?? [];

        return featuresEnabled(flagsFilter, flags) || featuresEnabled(orFlagsFilter, flags);
    });

    constructor() {
        effect(() => {
            /** Track changes to featureEnabled in order to run updateView */
            this.featureEnabled();

            if (this.featureFlagElse() !== null) {
                assertTemplate("featureFlagElse", this.featureFlagElse());
                this._elseTemplateRef = this.featureFlagElse();
                this._elseViewRef = null; // Clear previous if any
            }

            this.updateView();
        });
    }

    private updateView(): void {
        if (this.featureEnabled()) {
            if (this._thenTemplateRef) {
                this.vcr.clear();
                this._elseViewRef = null; // Clear else view
                this._thenViewRef = this.vcr.createEmbeddedView(this._thenTemplateRef);
            }
        } else if (this._elseTemplateRef) {
            this.vcr.clear();
            this._thenViewRef = null;
            this._elseViewRef = this.vcr.createEmbeddedView(this._elseTemplateRef);
        } else {
            this.vcr.clear();
            this._thenViewRef = null;
            this._elseViewRef = null;
        }
    }
}

function assertTemplate(property: string, templateRef: TemplateRef<unknown> | null): void {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
        throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
    }
}
